'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import { useBackgroundTheme } from './use-background-theme';

/**
 * WarpStarfield — Warp-Speed Sternenfeld
 *
 * Technik: Canvas 2D, 30fps throttle, IntersectionObserver pause
 * CPU-Impact: Low (einfache Punkt/Linie-Berechnungen, ~200 Partikel)
 *
 * Verwendung:
 *   <WarpStarfieldBackground speed={0.5}>
 *     <div className="relative z-10">Content</div>
 *   </WarpStarfieldBackground>
 */
function WarpStarfieldBackground({
  className,
  children,
  speed = 1,
  opacity = 1,
  starCount = 220,
  color,               // auto from theme if not given
  warp = false,        // true = schneller Warp-Effekt
  variant = 'auto',
  ...props
}) {
  const isDark = useBackgroundTheme(variant);
  const resolvedColor = color ?? (isDark ? '#ffffff' : '#1e293b');
  const bgClass = isDark ? 'bg-slate-950' : 'bg-white';
  const canvasRef = React.useRef(null);
  const animRef = React.useRef(null);
  const starsRef = React.useRef([]);
  const lastFrameRef = React.useRef(0);
  const pausedRef = React.useRef(false);
  const FPS = 30;
  const INTERVAL = 1000 / FPS;

  const baseSpeed = warp ? 4 : 0.6;
  const effectiveSpeed = baseSpeed * speed;

  const initStars = React.useCallback((w, h) => {
    starsRef.current = Array.from({ length: starCount }, () => ({
      x: (Math.random() - 0.5) * w,
      y: (Math.random() - 0.5) * h,
      z: Math.random() * w,
      pz: 0,
    }));
  }, [starCount]);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: false });

    let w = canvas.offsetWidth;
    let h = canvas.offsetHeight;
    canvas.width = w;
    canvas.height = h;
    initStars(w, h);

    // IntersectionObserver: pause when off-screen
    const observer = new IntersectionObserver(
      ([entry]) => { pausedRef.current = !entry.isIntersecting; },
      { threshold: 0.01 },
    );
    observer.observe(canvas);

    // Resize
    const ro = new ResizeObserver(() => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w;
      canvas.height = h;
      initStars(w, h);
    });
    ro.observe(canvas);

    // Parse color to rgb
    const hex = resolvedColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const draw = (timestamp) => {
      animRef.current = requestAnimationFrame(draw);
      if (pausedRef.current) return;
      if (timestamp - lastFrameRef.current < INTERVAL) return;
      lastFrameRef.current = timestamp;

      // Reduce motion check
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      const cx = w / 2;
      const cy = h / 2;

      ctx.fillStyle = isDark ? 'rgba(2,6,23,0.25)' : 'rgba(255,255,255,0.35)'; // slight trail
      ctx.fillRect(0, 0, w, h);

      const stars = starsRef.current;
      for (let i = 0; i < stars.length; i++) {
        const s = stars[i];
        s.pz = s.z;
        s.z -= effectiveSpeed * 2;
        if (s.z <= 0) {
          s.x = (Math.random() - 0.5) * w;
          s.y = (Math.random() - 0.5) * h;
          s.z = w;
          s.pz = w;
        }

        const scale = w / s.z;
        const px = s.x * scale + cx;
        const py = s.y * scale + cy;

        if (px < 0 || px > w || py < 0 || py > h) continue;

        const prevScale = w / s.pz;
        const ppx = s.x * prevScale + cx;
        const ppy = s.y * prevScale + cy;

        const brightness = 1 - s.z / w;
        const size = Math.max(0.5, scale * 0.5);

        ctx.strokeStyle = `rgba(${r},${g},${b},${brightness * opacity})`;
        ctx.lineWidth = size;
        ctx.beginPath();
        ctx.moveTo(ppx, ppy);
        ctx.lineTo(px, py);
        ctx.stroke();
      }
    };

    animRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animRef.current);
      observer.disconnect();
      ro.disconnect();
    };
  }, [speed, starCount, resolvedColor, opacity, warp, isDark, effectiveSpeed, initStars]);

  return (
    <div
      data-slot="warp-starfield-background"
      className={cn('relative size-full overflow-hidden', bgClass, className)}
      {...props}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 size-full"
      />
      {children && (
        <div className="relative z-10 size-full">{children}</div>
      )}
    </div>
  );
}

export { WarpStarfieldBackground };
